import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { RouterProvider, createRouter } from "@tanstack/react-router";
import { httpBatchLink } from "@trpc/client";
import { PostHogProvider } from "posthog-js/react";
import { trpc } from "./lib/trpc";
import { routeTree } from "./routeTree.gen";
import { ThemeProvider } from "./lib/theme";
import { PostHogIdentifier } from "./components/PostHogIdentifier";
import { CommunityProvider } from "./contexts/CommunityContext";
import { Toaster } from "./components/ui/sonner";

const queryClient = new QueryClient();

const trpcClient = trpc.createClient({
  links: [
    httpBatchLink({
      url: `${import.meta.env.VITE_SERVER_URL}/api/trpc`,
      fetch(url, options) {
        return fetch(url, {
          ...options,
          credentials: "include",
        });
      },
    }),
  ],
});

const router = createRouter({ routeTree });

// Type augmentation for TanStack Router
declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}

const POSTHOG_KEY = import.meta.env.VITE_POSTHOG_API_KEY || "";
const POSTHOG_HOST =
  import.meta.env.VITE_POSTHOG_HOST || "https://app.posthog.com";
const APP_VERSION = __APP_VERSION__;

function App() {
  return (
    <ThemeProvider>
      <PostHogProvider
        apiKey={POSTHOG_KEY}
        options={{
          api_host: POSTHOG_HOST,
          session_recording: {
            recordCrossOriginIframes: true,
          },
          capture_pageview: true,
          capture_pageleave: true,
          loaded: (posthog) => {
            if (import.meta.env.DEV) {
              posthog.debug();
            }
            // Set app version for source map matching
            posthog.register({
              app_version: APP_VERSION,
            });
          },
        }}
      >
        <trpc.Provider client={trpcClient} queryClient={queryClient}>
          <QueryClientProvider client={queryClient}>
            <CommunityProvider>
              <PostHogIdentifier />
              <RouterProvider router={router} />
              <Toaster />
            </CommunityProvider>
          </QueryClientProvider>
        </trpc.Provider>
      </PostHogProvider>
    </ThemeProvider>
  );
}

export default App;
